// Auth UI. Legacy: openAuth/switchAuthTab/submitAuth/submitSignUp/
// submitForgotPassword (snapshot L8525-8562 markup, L8758-8864 logic).
// Single modal with three tabs (Sign in / Sign up / Forgot), restyled to the
// dark-slate theme via the shared openModal() helper.
//
// Test contract (test/auth.mjs):
//   - opening exposes `.modal-backdrop` (openModal stamps it on the overlay)
//   - `.modal-title` reads /sign in/i on the sign-in tab
//   - `.modal-backdrop input` count > 0
//   - a button with accessible name 'Sign in' exists on the sign-in tab
import { supabase } from './supabase'
import { reloadForAdState, hasStaticMediavineTag } from './../ads/providers/mediavine'
import { setStoredPaid } from './../ads/paidFlag'
import { checkOrgTier, effectiveTier } from './org'
import { pendingPlan, pendingBilling } from './../deeplink'
import { startCheckout } from './../billing'
import { PRODUCT } from './../product'
import { state } from './../state'
import { el, openModal, closeModal, toast } from './../ui/dom'

type Tab = 'signin' | 'signup' | 'forgot'

export function closeAuth(): void { closeModal() }

export function openAuth(tab: Tab = 'signin', opts: { email?: string; message?: string; /** after a successful sign-in/sign-up (e.g. return to /account/) */ onSignedIn?: () => void } = {}): void {
  let current: Tab = tab

  // ----- tabs (Sign In / Sign Up) -----
  const tabs = el('div', 'auth-tabs')
  const tabSignIn = el('button', 'auth-tab', 'Sign In')
  const tabSignUp = el('button', 'auth-tab', 'Sign Up')
  tabs.append(tabSignIn, tabSignUp)

  // ----- messages -----
  const errBox = el('div', 'auth-error')
  const okBox = el('div', 'auth-success')
  errBox.style.display = 'none'
  okBox.style.display = 'none'
  const setErr = (t: string) => { errBox.textContent = t; errBox.style.display = t ? '' : 'none'; okBox.style.display = 'none' }
  const setOk = (t: string) => { okBox.textContent = t; okBox.style.display = t ? '' : 'none'; errBox.style.display = 'none' }
  const clearMsg = () => { setErr(''); setOk('') }

  // ----- shared inputs -----
  const email = el('input', 'auth-input') as HTMLInputElement
  email.type = 'email'; email.placeholder = 'Email'; email.autocomplete = 'email'
  if (opts.email) email.value = opts.email // ?email= deep-link prefill
  const pass = el('input', 'auth-input') as HTMLInputElement
  pass.type = 'password'; pass.placeholder = 'Password'; pass.autocomplete = 'current-password'
  const confirm = el('input', 'auth-input') as HTMLInputElement
  confirm.type = 'password'; confirm.placeholder = 'Confirm password'; confirm.autocomplete = 'new-password'

  // marketing opt-in (sign up only)
  const marketingWrap = el('label', 'flex items-start gap-2 mb-3 cursor-pointer')
  const marketing = el('input') as HTMLInputElement
  // Unticked by default and SAVED with the account (marketing_opt_in): a pre-ticked
  // box is not consent, and the choice used to be dropped entirely (2026-10-12).
  marketing.type = 'checkbox'; marketing.checked = false; marketing.dataset.testid = 'signup-marketing'; marketing.className = 'accent-blue-500 mt-0.5 flex-shrink-0'
  const marketingTxt = el('span', 'text-xs text-slate-400 leading-snug', 'Keep me updated on new features and tips')
  marketingWrap.append(marketing, marketingTxt)
  // Sign-up: point to the privacy policy (GDPR transparency at the moment data is collected).
  const privacyNote = el('p', 'text-[11px] text-slate-500 leading-snug -mt-1 mb-3')
  if (PRODUCT.links.privacy) {
    privacyNote.append(document.createTextNode('By creating an account you agree to how we handle your data, as described in our '))
    const a = el('a', 'underline hover:text-slate-300', 'Privacy Policy') as HTMLAnchorElement
    a.href = PRODUCT.links.privacy; a.target = '_blank'; a.rel = 'noopener'
    privacyNote.append(a, document.createTextNode('.'))
    privacyNote.dataset.testid = 'signup-privacy'
  }

  // submit
  const submit = el('button', 'auth-btn') as HTMLButtonElement
  submit.type = 'button'

  // forgot password link (sign-in only)
  const forgotRow = el('p', 'text-center mt-2.5')
  const forgotLink = el('a', 'text-xs no-underline', 'Forgot password?')
  forgotLink.href = '#'
  forgotLink.style.color = '#3b82f6'
  forgotLink.addEventListener('click', (e) => { e.preventDefault(); current = 'forgot'; render() })
  forgotRow.append(forgotLink)

  // back-to-signin link (forgot only)
  const backRow = el('p', 'text-center mt-2.5')
  const backLink = el('a', 'text-xs no-underline', 'Back to sign in')
  backLink.href = '#'
  backLink.style.color = '#3b82f6'
  backLink.addEventListener('click', (e) => { e.preventDefault(); current = 'signin'; render() })
  backRow.append(backLink)

  const sub = el('p', 'auth-sub', 'Save your designs to the cloud. Free account.')

  const body = el('div')
  body.append(sub, tabs, errBox, okBox, email, pass, confirm, marketingWrap, privacyNote, submit, forgotRow, backRow)

  const show = (cond: boolean, node: HTMLElement) => { node.style.display = cond ? '' : 'none' }

  // openModal stamps a `.modal-title` span; we keep a handle to update it per tab.
  let titleSpan: HTMLElement | null = null

  const render = () => {
    tabSignIn.classList.toggle('active', current === 'signin')
    tabSignUp.classList.toggle('active', current === 'signup')

    show(current !== 'forgot', pass)
    show(current === 'signup', confirm)
    show(current === 'signup', marketingWrap)
    show(current === 'signup' && !!PRODUCT.links.privacy, privacyNote)
    show(current === 'signin', forgotRow)
    show(current === 'forgot', backRow)
    show(current !== 'forgot', tabs)

    submit.textContent = current === 'signin' ? 'Sign in' : current === 'signup' ? 'Create Account' : 'Send Reset Link'
    // .modal-title alias must read /sign in/i on the signin tab (auth.mjs)
    if (titleSpan) titleSpan.textContent = current === 'signin' ? 'Sign in' : current === 'signup' ? 'Create account' : 'Reset password'
    clearMsg()
  }

  tabSignIn.addEventListener('click', () => { current = 'signin'; render() })
  tabSignUp.addEventListener('click', () => { current = 'signup'; render() })

  submit.addEventListener('click', async () => {
    submit.disabled = true
    try {
      if (current === 'signin') {
        const { data, error } = await supabase.auth.signInWithPassword({ email: email.value.trim(), password: pass.value })
        if (error) return setErr(error.message)
        state.currentUser = data.user
        closeAuth()
        if (opts.onSignedIn) { opts.onSignedIn(); return }
        if (hasStaticMediavineTag() && data.session) {
          // Know the plan before reloading, so the head marker never lets ads
          // through for a Business team member (plan comes from the org).
          await checkOrgTier(data.session.access_token)
          setStoredPaid(effectiveTier() !== 'free')
        }
        if (reloadForAdState()) return // Mediavine page: reload so the account's ad state applies
        toast('Signed in')
      } else if (current === 'signup') {
        if (pass.value.length < 6) return setErr('Password must be at least 6 characters')
        if (pass.value !== confirm.value) return setErr('Passwords do not match')
        const { data, error } = await supabase.auth.signUp({ email: email.value.trim(), password: pass.value, options: { data: { marketing_opt_in: marketing.checked } } })
        if (error) return setErr(error.message)
        if (data.user && data.user.identities && data.user.identities.length === 0) return setErr('An account with this email already exists')
        if (data.session) {
          state.currentUser = data.user
          closeAuth()
          // ?plan=pro|business deep link: go straight to Stripe checkout (legacy parity).
          const plan = pendingPlan()
          if (plan) void startCheckout(plan, data.session.access_token, true, pendingBilling(), 'plan_link')
        } else setOk('Check your email for a confirmation link!')
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(email.value.trim(), { redirectTo: PRODUCT.editorUrl })
        if (error) setErr(error.message)
        else setOk('Check your email for a reset link!')
      }
    } finally {
      submit.disabled = false
    }
  })

  const onEnter = (e: KeyboardEvent) => { if (e.key === 'Enter') submit.click() }
  email.addEventListener('keydown', onEnter)
  pass.addEventListener('keydown', onEnter)
  confirm.addEventListener('keydown', onEnter)

  openModal({ title: 'Sign in', body, width: '380px', testid: 'auth-modal' })
  // grab the title span openModal created so we can keep it in sync per tab
  titleSpan = document.querySelector('.modal-title') as HTMLElement

  render()
  if (opts.message) setOk(opts.message) // funnel prompts: why an account is needed (render() clears messages)
  email.focus()
}
